// src/shared_new/sections/PlayBookSection.jsx
// PlayBook Section - Manufacturing parameters configuration

import React from "react";
import { Box, Divider, Grid, Autocomplete, TextField, Typography, alpha } from "@mui/material";
import SliderModule from "../modules/SliderModule";
import { theme } from "../../design/theme";

const { perlColors, perlTypography } = theme.custom;

/**
 * PlayBookSection - Complete PlayBook configuration section
 *
 * @param {Object} props
 * @param {Object} props.data - PlayBook data object
 * @param {Function} props.onChange - Callback: (field, value) => void
 * @param {Object} props.ranges - Slider ranges: { moisture: {min, max, step}, process: {}, packaging: {}, ohp: {} }
 * @param {Array} props.primaryPackagingOptions - List of primary packaging options
 * @param {Array} props.secondaryPackagingOptions - List of secondary packaging options
 * @param {Array} props.tertiaryPackagingOptions - List of tertiary packaging options
 * @param {Array} props.storageConditionsOptions - List of storage conditions
 * @param {Array} props.regulatoryOptions - List of regulatory compliance options
 */
export default function PlayBookSection({
  data = {},
  onChange,
  ranges = {
    moisture: { min: 0, max: 100, step: 0.5 },
    process: { min: 0, max: 20, step: 0.5 },
    packaging: { min: 0, max: 20, step: 0.5 },
    ohp: { min: 0, max: 30, step: 0.5 },
  },
  primaryPackagingOptions = [],
  secondaryPackagingOptions = [],
  tertiaryPackagingOptions = [],
  storageConditionsOptions = [],
  regulatoryOptions = [],
  readOnlyOhp = false,
}) {
  // Process Parameters sliders
  const processSliders = [
    {
      key: "moisture",
      label: "Moisture Content",
      value: parseFloat(data.moisture_content || 0),
      min: ranges.moisture.min,
      max: ranges.moisture.max,
      step: ranges.moisture.step,
      unit: "%",
      onChange: (e, v) => onChange("moisture_content", v),
    },
    {
      key: "process_loss",
      label: "Process Loss",
      value: parseFloat(data.process_loss || 0),
      min: ranges.process.min,
      max: ranges.process.max,
      step: ranges.process.step,
      unit: "%",
      onChange: (e, v) => onChange("process_loss", v),
    },
    {
      key: "packaging_loss",
      label: "Packaging Loss",
      value: parseFloat(data.packaging_loss || 0),
      min: ranges.packaging.min,
      max: ranges.packaging.max,
      step: ranges.packaging.step,
      unit: "%",
      onChange: (e, v) => onChange("packaging_loss", v),
    },
    {
      key: "ohp",
      label: "Overheads & Profit (OHP)",
      value: parseFloat(data.ohp_percentage || 0),
      min: ranges.ohp.min,
      max: ranges.ohp.max,
      step: ranges.ohp.step,
      unit: "%",
      onChange: (e, v) => onChange("ohp_percentage", v),
    },
  ];

  return (
    <Box>
      {/* Process Parameters */}
      <SliderModule title="Process Parameters" sliders={readOnlyOhp ? processSliders.filter((slider) => slider.key !== "ohp") : processSliders} columns={readOnlyOhp ? 3 : 4} />
      {readOnlyOhp && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" color="text.secondary">Calculated from last completed quote; recalculate after edits.</Typography>
          <Typography variant="body2">Overheads &amp; Profit (calculated): {Number.isFinite(data.ohp_amount) ? `$${data.ohp_amount.toFixed(4)} per pack` : "—"}</Typography>
          <Typography variant="body2" color="text.secondary">{Number.isFinite(data.ohp_percentage) ? `${data.ohp_percentage.toFixed(2)}% of material + process + packaging` : "Calculated from the completed quote"}</Typography>
          {Number.isFinite(data.annual_volume) && Number.isFinite(data.monthly_pack_quantity) && (
            <Typography variant="body2" color="text.secondary">Volume used: {data.annual_volume.toLocaleString()} finished packs/year; monthly average: {data.monthly_pack_quantity.toLocaleString(undefined, { maximumFractionDigits: 6 })} packs.</Typography>
          )}
        </Box>
      )}

      <Divider sx={{ my: 3, borderColor: perlColors.border }} />

      {/* Packaging Details */}
      <Typography
        variant="subtitle2"
        fontWeight={600}
        sx={{ mb: 2, color: perlColors.textPrimary }}
      >
        Packaging Details
      </Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={4}>
          <Autocomplete
            options={primaryPackagingOptions}
            value={data.primary_packaging || null}
            onChange={(e, newValue) => onChange("primary_packaging", newValue)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Primary Packaging"
                InputLabelProps={{ shrink: true }}
                size="small"
              />
            )}
            size="small"
            fullWidth
            sx={{ minWidth: 300 }}
            componentsProps={{
              paper: {
                sx: {
                  bgcolor: perlColors.surface,
                  "& .MuiAutocomplete-listbox": {
                    "& .MuiAutocomplete-option": {
                      color: perlColors.textPrimary,
                      "&:hover": {
                        bgcolor: alpha(perlColors.white, 0.08),
                      },
                      "&[aria-selected='true']": {
                        bgcolor: alpha(perlColors.white, 0.12),
                      },
                    },
                  },
                },
              },
            }}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <Autocomplete
            options={secondaryPackagingOptions}
            value={data.secondary_packaging || null}
            onChange={(e, newValue) => onChange("secondary_packaging", newValue)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Secondary Packaging"
                InputLabelProps={{ shrink: true }}
                size="small"
              />
            )}
            size="small"
            fullWidth
            sx={{ minWidth: 300 }}
            componentsProps={{
              paper: {
                sx: {
                  bgcolor: perlColors.surface,
                  "& .MuiAutocomplete-listbox": {
                    "& .MuiAutocomplete-option": {
                      color: perlColors.textPrimary,
                      "&:hover": {
                        bgcolor: alpha(perlColors.white, 0.08),
                      },
                      "&[aria-selected='true']": {
                        bgcolor: alpha(perlColors.white, 0.12),
                      },
                    },
                  },
                },
              },
            }}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <Autocomplete
            options={tertiaryPackagingOptions}
            value={data.tertiary_packaging || null}
            onChange={(e, newValue) => onChange("tertiary_packaging", newValue)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Tertiary Packaging"
                InputLabelProps={{ shrink: true }}
                size="small"
              />
            )}
            size="small"
            fullWidth
            sx={{ minWidth: 300 }}
            componentsProps={{
              paper: {
                sx: {
                  bgcolor: perlColors.surface,
                  "& .MuiAutocomplete-listbox": {
                    "& .MuiAutocomplete-option": {
                      color: perlColors.textPrimary,
                      "&:hover": {
                        bgcolor: alpha(perlColors.white, 0.08),
                      },
                      "&[aria-selected='true']": {
                        bgcolor: alpha(perlColors.white, 0.12),
                      },
                    },
                  },
                },
              },
            }}
          />
        </Grid>
      </Grid>

      <Divider sx={{ my: 3, borderColor: perlColors.border }} />

      {/* Product Specifications */}
      {/* <Typography
        variant="subtitle2"
        fontWeight={600}
        sx={{ mb: 2, color: perlColors.textPrimary }}
      >
        Product Specifications
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={4}>
          <TextField
            fullWidth
            label="Shelf Life (months)"
            type="number"
            value={data.shelf_life || ""}
            onChange={(e) => onChange("shelf_life", e.target.value)}
            InputLabelProps={{ shrink: true }}
            size="small"
            sx={{ minWidth: 300 }}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <Autocomplete
            options={storageConditionsOptions}
            value={data.storage_conditions || null}
            onChange={(e, newValue) => onChange("storage_conditions", newValue)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Storage Conditions"
                InputLabelProps={{ shrink: true }}
                size="small"
              />
            )}
            size="small"
            fullWidth
            sx={{ minWidth: 300 }}
            componentsProps={{
              paper: {
                sx: {
                  bgcolor: perlColors.surface,
                  "& .MuiAutocomplete-listbox": {
                    "& .MuiAutocomplete-option": {
                      color: perlColors.textPrimary,
                      "&:hover": {
                        bgcolor: alpha(perlColors.white, 0.08),
                      },
                      "&[aria-selected='true']": {
                        bgcolor: alpha(perlColors.white, 0.12),
                      },
                    },
                  },
                },
              },
            }}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <Autocomplete
            options={regulatoryOptions}
            value={data.regulatory_compliance || null}
            onChange={(e, newValue) => onChange("regulatory_compliance", newValue)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Regulatory Compliance"
                InputLabelProps={{ shrink: true }}
                size="small"
              />
            )}
            size="small"
            fullWidth
            sx={{ minWidth: 300 }}
            componentsProps={{
              paper: {
                sx: {
                  bgcolor: perlColors.surface,
                  "& .MuiAutocomplete-listbox": {
                    "& .MuiAutocomplete-option": {
                      color: perlColors.textPrimary,
                      "&:hover": {
                        bgcolor: alpha(perlColors.white, 0.08),
                      },
                      "&[aria-selected='true']": {
                        bgcolor: alpha(perlColors.white, 0.12),
                      },
                    },
                  },
                },
              },
            }}
          />
        </Grid>
      </Grid> */}
    </Box>
  );
}
